import * as React from "react";
import { bindActionCreators } from "redux";
import styled from "styled-components";
import { connect } from "react-redux";
import moment, { Moment } from "moment";
import { withRouter } from "react-router";

import withGranted, { IWithGrantedProps } from "../HOC/WithGranted";
import { createStructuredSelector } from "reselect";
import { IGlobalStore } from "../../_reducers/reducers";
import { IShortLocationInfo } from "../../_types/common";
import * as actionsSearch from "../../_actions/search";
import * as layoutActions from "../../_actions/layout";
import * as flatFilesApi from "../../_api/flatFiles";
import { notifySwagger, notify } from "../../_actions/notification";

import {
  DatePicker,
  Table,
  LocationSelectorComponent,
  Pagination,
  MainContainer,
  RunningLineLoader,
  Skeleton,
  Button,
  Article
} from "../../components";
import { IFlatLocationOption } from "../../_reducers/current-user";
import { clientUnitFilterOptions } from "../../_utils/clientUnitFilter";

const Position = {
  Container: styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
  `,

  Body: styled.div`
    height: 100%;
    flex: 1 1 100%;
  `,

  Footer: styled.div`
    flex: 0 0;
  `,

  Head: styled<{ className?: string }>(({ className, children }) => (
    <header className={className}>
      <div>{children}</div>
    </header>
  ))`
    padding: 20px;
    > div {
      margin: -7.5px;
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
    }
  `,

  HeadElement: styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    & > * {
      margin: 7.5px;
    }
  `,

  Filter: styled.div`
    width: 260px;
  `
};

interface IFlatFileDto {
  id: number;
  flatFileIds?: number[]; // Array of all flat file IDs in this group
  clientUnitId: number;
  locationName: string;
  vendorId: number;
  vendorName: string;
  dateRangeStart: string;
  dateRangeEnd: string;
  creationTime: string;
  isDownloaded: boolean;
}

interface IGetFlatFilesParams {
  startDate?: moment.Moment;
  endDate?: moment.Moment;
  sorting?: string;
  skip?: number;
  perPage?: number;
}

interface IFlatFilesListProps
  extends IWithGrantedProps,
    IConnectedProps,
    IConnectedActions {}

interface IFlatFilesListState {
  sorting: string;
  skip: number;
  flatFilesStatus: string;
  flatFiles?: IFlatFileDto[];
  totalCount: number;
  clientUnitFilter: number | null;
  startDate: Moment | null;
  endDate: Moment | null;
  focusedInput: string;
  isDownloading: { [key: number]: boolean };
}

class FlatFilesList extends React.Component<
  IFlatFilesListProps,
  IFlatFilesListState
> {
  static renderPreloader(perPage: number, status: string): React.Component[] {
    const rows = [];
    const elementsCount = status === "loading" ? perPage : 0;

    for (let i = 0; i < elementsCount; i++) {
      rows.push(
        <Table.Row
          key={i}
          rows={perPage}
          cells={[
            <Skeleton.Line key={`flat-files-preloader-${i}-0`} size="md" />,
            <Skeleton.Line key={`flat-files-preloader-${i}-1`} size="xs" />,
            <Skeleton.Line key={`flat-files-preloader-${i}-2`} size="md" />,
            <Skeleton.Line key={`flat-files-preloader-${i}-3`} size="sm" />,
            <Skeleton.Line key={`flat-files-preloader-${i}-4`} size="sm" />,
            <Skeleton.Line key={`flat-files-preloader-${i}-5`} size="md" />,
            <Skeleton.Line key={`flat-files-preloader-${i}-6`} size="sm" />
          ]}
        />
      );
    }
    return rows;
  }

  static calculateWeeks(startDate: string, endDate: string): number {
    if (!startDate || !endDate) return 0;
    const start = moment(startDate);
    const end = moment(endDate);
    return Math.ceil(end.diff(start, 'weeks', true));
  }

  constructor(props) {
    super(props);

    this.state = {
      sorting: "creationTime DESC",
      skip: 0,
      flatFilesStatus: "",
      totalCount: 0,
      clientUnitFilter: null,
      startDate: null,
      endDate: null,
      focusedInput: null,
      isDownloading: {}
    };

    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleSortChanged = this.handleSortChanged.bind(this);
    this.getFlatFiles = this.getFlatFiles.bind(this);
    this.handleClientFilterChange = this.handleClientFilterChange.bind(this);
    this.handleDateChanged = this.handleDateChanged.bind(this);
    this.handleDownloadClick = this.handleDownloadClick.bind(this);

    this.props.setSearch("/flat-files", () => {
      this.setState({ skip: 0 }, () => {
        this.getFlatFiles();
      });
    });
  }

  componentDidMount() {
    this.getFlatFiles();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.locationInfo.id !== this.props.locationInfo.id) {
      this.setState({ clientUnitFilter: null }, () => {
        this.getFlatFiles();
      });
    }
  }

  getFlatFiles(params: IGetFlatFilesParams = {}) {
    const skip =
      Number.isInteger(params.skip) && params.skip >= 0
        ? params.skip
        : this.state.skip;
    const sort = params.sorting || this.state.sorting;
    const startDate = params.startDate !== undefined ? params.startDate : this.state.startDate;
    const endDate = params.endDate !== undefined ? params.endDate : this.state.endDate;
    const perPage = params.perPage || this.props.perPage;

    let statusStartKey = "loading";
    let statusFinishKey = "loaded";
    if (params.sorting) {
      statusStartKey = "sorting";
      statusFinishKey = "sorted";
    }

    this.setState({ flatFilesStatus: statusStartKey });

    flatFilesApi
      .getFlatFiles(
        startDate,
        endDate,
        this.state.clientUnitFilter,
        null,
        null,
        skip,
        perPage,
        sort
      )
      .then(response => {
        this.setState({
          flatFilesStatus: statusFinishKey,
          flatFiles: response.items as any,
          totalCount: response.totalCount
        });
      })
      .catch(e => {
        this.props.notifySwagger(e, "error");
        this.setState({ flatFilesStatus: "error" });
      });
  }

  handleChangePage(pageInfo) {
    const { perPage, skip } = pageInfo;

    this.setState({ skip }, () => {
      this.getFlatFiles({ perPage, skip });
      this.props.setPerPage(perPage);
    });
  }

  handleSortChanged(sorting) {
    this.setState({ sorting });
    this.getFlatFiles({ sorting });
  }

  handleClientFilterChange(value: IFlatLocationOption) {
    this.setState({ clientUnitFilter: value?.id || null, skip: 0 }, () => {
      this.getFlatFiles();
    });
  }

  handleDateChanged(range: { startDate: Moment; endDate: Moment }) {
    const startDate = range.startDate
      ? range.startDate.startOf("day")
      : null;
    const endDate = range.endDate ? range.endDate.startOf("day") : null;

    this.setState({ startDate, endDate, skip: 0 }, () => {
      this.getFlatFiles();
    });
  }

  handleDownloadClick(flatFileId: number, flatFileIds: number[]) {
    // Use the group IDs if available, otherwise use single ID
    const idsToDownload = flatFileIds && flatFileIds.length > 0 ? flatFileIds : [flatFileId];
    
    // Set downloading state for this specific file
    this.setState(prevState => ({
      isDownloading: { ...prevState.isDownloading, [flatFileId]: true }
    }));

    // Export flat file as CSV
    flatFilesApi
      .exportFlatFilesToCsv(idsToDownload)
      .then(({ blob, filename }) => {
        // Trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Mark files as downloaded
        return flatFilesApi.markFlatFilesAsDownloaded(idsToDownload);
      })
      .then(() => {
        this.setState(prevState => ({
          isDownloading: { ...prevState.isDownloading, [flatFileId]: false }
        }));
        this.props.notify("Flat file downloaded successfully!", "success");
        // Refresh the list to update the download status
        this.getFlatFiles();
      })
      .catch(err => {
        this.setState(prevState => ({
          isDownloading: { ...prevState.isDownloading, [flatFileId]: false }
        }));
        const errorMessage = err?.message || err;
        if (typeof errorMessage === "string") {
          this.props.notify(errorMessage, "error");
        } else {
          this.props.notify("Error downloading flat file", "error");
        }
      });
  }

  render() {
    const hasData = this.state.flatFiles && this.state.flatFiles.length > 0;
    const isLoaded = this.state.flatFilesStatus === "loaded" || 
                     this.state.flatFilesStatus === "sorted";

    return (
      <MainContainer>
        <Position.Container>
          <Position.Head>
            <Position.HeadElement>
              <Article.Text>
                <strong>FLAT FILES</strong>
              </Article.Text>
            </Position.HeadElement>
            <Position.HeadElement>
              <DatePicker
                compact
                type="range"
                startDateId="startDate"
                endDateId="endDate"
                isOutsideRange={() => false}
                startDate={this.state.startDate}
                endDate={this.state.endDate}
                onDatesChange={this.handleDateChanged}
                focusedInput={this.state.focusedInput}
                keepOpenOnDateSelect
                showPeriods
                onFocusChange={focusedInput => {
                  this.setState({ focusedInput });
                }}
              />
              <Position.Filter>
                <LocationSelectorComponent
                  customFilter={clientUnitFilterOptions}
                  locationOptions={this.props.locationOptions}
                  onLocationChange={this.handleClientFilterChange}
                  selectedLocation={this.state.clientUnitFilter}
                  placeholder="All Clients"
                />
              </Position.Filter>
            </Position.HeadElement>
          </Position.Head>
          <Position.Body>
            <RunningLineLoader
              isActive={
                this.state.flatFilesStatus === "sorting" ||
                this.state.flatFilesStatus === "loading"
              }
            />
            <Table
              fullHeight
              columns={[
                { name: "locationName", label: "Client", sortable: true, size: "lg" },
                { name: "numberOfWeeks", label: "# of Weeks", sortable: false, size: "sm" },
                { name: "vendorName", label: "Vendor", sortable: true, size: "lg" },
                { name: "dateRangeStart", label: "Start Date", sortable: true, size: "md" },
                { name: "dateRangeEnd", label: "End Date", sortable: true, size: "md" },
                { name: "creationTime", label: "Date Processed", sortable: true, size: "md" },
                { name: "actions", label: "Download", sortable: false, size: "sm" }
              ]}
            >
              <Table.Header
                sortBy={this.state.sorting}
                onSortChanged={this.handleSortChanged}
              />
              <Table.Body>
                {isLoaded &&
                  (hasData ? (
                    this.state.flatFiles.map((flatFile, index) => {
                      const isDownloading = this.state.isDownloading[flatFile.id] || false;
                      const numberOfWeeks = FlatFilesList.calculateWeeks(
                        flatFile.dateRangeStart,
                        flatFile.dateRangeEnd
                      );

                      return (
                        <Table.Row
                          key={`${index}-${flatFile.id}`}
                          cells={[
                            flatFile.locationName || "N/A",
                            numberOfWeeks > 0 ? numberOfWeeks.toString() : "N/A",
                            flatFile.vendorName || "N/A",
                            flatFile.dateRangeStart
                              ? moment.utc(flatFile.dateRangeStart).format("MM/DD/YYYY")
                              : "N/A",
                            flatFile.dateRangeEnd
                              ? moment.utc(flatFile.dateRangeEnd).format("MM/DD/YYYY")
                              : "N/A",
                            flatFile.creationTime
                              ? moment(flatFile.creationTime).format("MM/DD/YYYY HH:mm")
                              : "N/A",
                            <Button
                              key={`download-${flatFile.id}`}
                              onClick={() => this.handleDownloadClick(flatFile.id, flatFile.flatFileIds)}
                              primary
                              disabled={isDownloading}
                              preloader={isDownloading}
                            >
                              Download
                            </Button>
                          ]}
                        />
                      );
                    })
                  ) : (
                    <Table.Row
                      key={0}
                      colSpan="7"
                      rows={this.props.perPage}
                      cells={[
                        <span key="0">
                          There are no flat files within the parameters.
                        </span>
                      ]}
                    />
                  ))}
                {this.state.flatFilesStatus === "loading" &&
                  FlatFilesList.renderPreloader(
                    this.props.perPage,
                    this.state.flatFilesStatus
                  )}
              </Table.Body>
            </Table>
          </Position.Body>
          <Pagination
            skip={this.state.skip}
            perPage={this.props.perPage}
            count={this.state.totalCount}
            onChange={this.handleChangePage}
          />
        </Position.Container>
      </MainContainer>
    );
  }
}

interface IConnectedProps {
  locationInfo?: IShortLocationInfo;
  locationOptions: IFlatLocationOption[];
  perPage: number;
}

interface IConnectedActions {
  setSearch: typeof actionsSearch.set;
  setPerPage: typeof layoutActions.setPerPage;
  notifySwagger: typeof notifySwagger;
  notify: typeof notify;
}

export default withRouter(
  connect(
    createStructuredSelector<IGlobalStore, IConnectedProps>({
      perPage: state => state.layout.perPage,
      locationInfo: state => state.currentUser.locationInfo,
      locationOptions: state => state.currentUser.locationOptions
    }),
    dispatch =>
      bindActionCreators(
        {
          setSearch: actionsSearch.set,
          setPerPage: layoutActions.setPerPage,
          notifySwagger,
          notify
        },
        dispatch
      )
  )(withGranted(FlatFilesList))
);